/**
 * Default mapping to iterate over items received
 * @param {*} doc - The current document
 * @param {*} data - The data provided by the document
 * @returns {*} The data mapped
 */
export const getDefaultMapping = (doc, data = {}) => ({
  id: doc.id,
  ...data,
  created_at: data.created_at && data.created_at.toDate(),
  updated_at: data.updated_at && data.updated_at.toDate(),
})


/**
 * Get all data
 * @param {*} ref - The ref document
 * @param {*} options - Options to filter
 * @returns {Array<*>} Array of items
 */
export const getAll = async (ref, options = {}) => {
  const {
    first,
    endAt,
    where,
    startAt,
    onlyItems,
    endBefore,
    limit = 50,
    startAfter,
    direction = 'desc',
    orderBy = 'created_at',
    map = getDefaultMapping,
  } = options
  let snapshots = ref

  if (startAt) {
    snapshots = snapshots.where(orderBy, '>', startAt)
  }

  if (endAt) {
    snapshots = snapshots.where(orderBy, '<', endAt)
  }

  console.log('--- orderBy', orderBy, direction)
  snapshots = snapshots.orderBy(orderBy, direction)

  if (startAfter) {
    snapshots = snapshots.startAfter(startAfter)
  } else if (endBefore) {
    snapshots = snapshots.endAt(endBefore)
  }

  if (where) {
    snapshots = where.inject(snapshots)
  }

  snapshots = await snapshots.limit(onlyItems ? limit : limit + 1).get()

  if (first) {
    return snapshots.empty ? null : map(snapshots.docs[0], snapshots.docs[0].data())
  }

  let result = await Promise.all(snapshots.docs.map((doc) => {
    const data = doc.data()

    return data ? map(doc, doc.data()) : null
  }))

  result = result.filter(x => x)

  if (onlyItems) {
    return result
  }

  const hasPrevious = !!startAfter
  const items = result.slice(0, limit)
  const hasMore = result.length > limit

  // Utiliser les valeurs des documents originaux (avant mapping) pour startAfter et endBefore
  const firstDoc = snapshots.docs[0]
  const lastDoc = snapshots.docs[items.length - 1]
  const firstDocData = firstDoc ? firstDoc.data() : null
  const lastDocData = lastDoc ? lastDoc.data() : null

  return {
    items,
    hasMore,
    hasPrevious,
    endBefore: hasPrevious && firstDocData ? firstDocData[orderBy] : '',
    startAfter: hasMore && lastDocData ? lastDocData[orderBy] : '',
  }
}


/**
 * Get one document
 * @param {*} ref - The ref document
 * @param {*} options - Options to filter
 * @returns {*} The item
 */
export const get = async (ref, options = {}) => {
  const {
    map = getDefaultMapping,
  } = options

  const snapshot = await ref.get()

  if (Array.isArray(snapshot.docs) && snapshot.docs.length > 0) {
    return map(snapshot.docs[0], snapshot.docs[0].data())
  }

  return snapshot.exists ? map(snapshot, snapshot.data()) : null
}


/**
 * Edit a document
 * @param {*} ref - The ref document
 * @param {*} values - Values to update
 * @param {*} options - Options for edition
 * @returns {*} The item
 */
export const edit = async (ref, values = {}, options = {}) => {
  await ref.update({
    ...values,
    updated_at: new Date(),
  })

  return get(ref, options)
}


/**
 * Add a document
 * @param {*} ref - The ref document
 * @param {*} values - The values of the document
 * @param {*} options - Options for addition
 * @returns {*} The item
 */
export const add = async (ref, values = {}, options = {}) => {
  const {
    onCreate,
    disableGet,
    ...restOptions
  } = options

  const snapshot = await ref.add({
    ...values,
    created_at: new Date(),
  })

  if (onCreate) {
    await onCreate(snapshot)
  }

  if (disableGet) {
    return snapshot
  }

  return get(ref.doc(snapshot.id), restOptions)
}


export const set = async (ref, values = {}, options = {}) => {
  const {
    onCreate,
    disableGet,
    ...restOptions
  } = options

  const snapshot = await ref.set({
    ...values,
    created_at: new Date(),
  })

  if (onCreate) {
    await onCreate(snapshot)
  }

  if (disableGet) {
    return snapshot
  }

  return get(ref, restOptions)
}
